import { useCallback, useEffect, useRef, useState } from 'react';
import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import {
  isTerminalImportStatus,
  rescueCsvImportProgressQuery,
  rescueStartImportMutation,
  type RescueCsvImportProgress,
  type RescueCsvImportProgressData,
  type RescueStartImportData,
} from '../lib/integrationQueries';
import { clearImportJobKey, writeImportJobKey } from '../lib/importJobCookie';

/**
 * How often we poll `rescueCsvImportProgress` while a job is in flight.
 * A rescue import is a background worker job (parse + apply up to 500
 * rows), so a few seconds between polls is plenty — short enough to feel
 * live, long enough not to hammer the API.
 */
const POLL_INTERVAL_MS = 2500;

/**
 * How many consecutive failed polls (a network/GraphQL error, or a `null`
 * progress result for an id that's gone) we tolerate before giving up and
 * surfacing a failed state with a retry affordance — rather than spinning
 * forever. Any good poll resets the count. At {@link POLL_INTERVAL_MS} this is
 * ~15s of sustained failure before we stop.
 */
const MAX_CONSECUTIVE_POLL_FAILURES = 6;

/**
 * Coarse state of the import-tracking flow, derived from the start
 * mutation + the latest progress poll:
 *   - `idle`      — nothing started yet.
 *   - `starting`  — the `rescueStartImport` mutation is in flight.
 *   - `tracking`  — job is QUEUED/RUNNING; we're polling for updates.
 *   - `succeeded` — worker finished successfully.
 *   - `failed`    — the start mutation errored, or the job ended FAILED.
 */
export type ImportProgressPhase = 'idle' | 'starting' | 'tracking' | 'succeeded' | 'failed';

export interface UseImportProgressResult {
  phase: ImportProgressPhase;
  /** Latest progress snapshot. Null until the start mutation returns. */
  progress: RescueCsvImportProgress | null;
  /**
   * Human-readable failure reason when `phase === 'failed'` — either the
   * start-mutation error or the worker's `terminalError`. Null otherwise.
   */
  errorMessage: string | null;
  /**
   * True when a single progress poll failed (network/GraphQL) but the job
   * is not in a terminal state — polling continues, the UI can show a
   * soft "couldn't refresh" hint rather than the prior snapshot going stale.
   */
  pollError: boolean;
  /** Kick off the worker for an uploaded S3 object and begin polling. */
  start: (uploadedFileKey: string) => Promise<void>;
  /** Resume polling an existing import by id (e.g. from the saved job cookie). */
  resume: (importId: string) => void;
  /**
   * True when a *resumed* import can't be found server-side (gone / aged out).
   * The caller should abandon the resume and return to a fresh upload; the
   * saved cookie has already been dropped.
   */
  importMissing: boolean;
  /** Stop polling and clear all state (close / "upload another"). */
  reset: () => void;
}

/**
 * Drives the post-upload half of the CSV import flow: hands the uploaded
 * S3 object off to the worker via `rescueStartImport`, then polls
 * `rescueCsvImportProgress` until the job reaches a terminal state.
 *
 * The caller PUTs the CSV to S3 itself (using a presigned URL) and then
 * passes the returned object key to `start`. Polling stops automatically
 * once the status is terminal, on `reset`, or on unmount.
 */
export default function useImportProgress(): UseImportProgressResult {
  // The progress row id we're polling. Null until the start mutation
  // returns; setting it flips the polling query on (it `skip`s on null).
  const [importId, setImportId] = useState<string | null>(null);
  // Seed snapshot from the start mutation so the UI shows QUEUED
  // immediately, before the first poll round-trip completes.
  const [startedSnapshot, setStartedSnapshot] = useState<RescueCsvImportProgress | null>(null);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  const [startImport] = useMutation<RescueStartImportData>(rescueStartImportMutation);

  // Polling is driven imperatively (see the effect below) rather than via the
  // `pollInterval` option: `start`/`resume` toggle `importId`, and resuming a
  // job after a prior `stopPolling` needs an explicit `startPolling` to re-arm.
  const { data, error, networkStatus, startPolling, stopPolling } = useQuery<RescueCsvImportProgressData>(
    rescueCsvImportProgressQuery,
    {
      variables: { id: importId ?? '' },
      skip: !importId,
      // Always hit the network — progress is mutated server-side by the
      // worker, so a cached snapshot would never advance.
      fetchPolicy: 'network-only',
      // `'all'` (vs the default `'none'`) keeps the polling ObservableQuery
      // alive when a poll errors. Under `'none'`, Apollo 3.x can tear the poll
      // down on the first error and never resume — stranding the spinner and
      // skipping the completion analytics. Errored/empty polls are instead
      // counted below and we give up after a bounded number of failures.
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    },
  );

  // Prefer the freshest poll result; fall back to the mutation's seed
  // snapshot until the first poll lands (or if a poll returned null).
  const progress = data?.rescueCsvImportProgress ?? startedSnapshot;

  // Count consecutive failed polls so we don't track forever when polling
  // keeps erroring or the progress row can't be found. We act only when a
  // poll *settles* (ready/error after an in-flight status), gated on the
  // previous network status so a single poll is counted once. A poll is "bad"
  // when it errored or returned null progress; any good poll resets the count.
  const [failedPolls, setFailedPolls] = useState(0);
  const prevNetworkStatusRef = useRef<NetworkStatus | null>(null);
  useEffect(() => {
    const prev = prevNetworkStatusRef.current;
    prevNetworkStatusRef.current = networkStatus;
    const settled = networkStatus === NetworkStatus.ready || networkStatus === NetworkStatus.error;
    const wasInFlight =
      prev === NetworkStatus.loading ||
      prev === NetworkStatus.poll ||
      prev === NetworkStatus.refetch ||
      prev === NetworkStatus.setVariables;
    if (!settled || !wasInFlight) {
      return;
    }
    const polledBadly = !!error || data?.rescueCsvImportProgress == null;
    setFailedPolls((n) => (polledBadly ? n + 1 : 0));
  }, [networkStatus, error, data]);

  const trackingStalled = failedPolls >= MAX_CONSECUTIVE_POLL_FAILURES;

  // Single source of truth for whether the progress query is polling. Poll
  // while we have a non-terminal import we haven't given up on; stop (and, on
  // a genuine terminal status, drop the saved job cookie since the user has
  // now seen the outcome) otherwise. `startPolling` is guarded by a ref so we
  // arm it once per import id rather than on every poll re-render.
  const pollingStartedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!importId) {
      stopPolling();
      pollingStartedForRef.current = null;
      return;
    }
    const terminal = progress ? isTerminalImportStatus(progress.status) : false;
    if (terminal || trackingStalled) {
      stopPolling();
      pollingStartedForRef.current = null;
      if (terminal) {
        clearImportJobKey();
      }
      return;
    }
    if (pollingStartedForRef.current !== importId) {
      startPolling(POLL_INTERVAL_MS);
      pollingStartedForRef.current = importId;
    }
  }, [importId, progress, trackingStalled, startPolling, stopPolling]);

  // A *resumed* import (no seed snapshot) whose latest settled poll returned
  // null with no error is gone server-side (aged out / unknown id) — a
  // permanent miss, not a transient blip. Surface it so the caller can drop
  // back to a fresh upload immediately rather than waiting out the stall cap,
  // and drop the now-useless saved cookie.
  const importMissing =
    !!importId && !startedSnapshot && !error && data !== undefined && data.rescueCsvImportProgress == null;
  useEffect(() => {
    if (importMissing) {
      clearImportJobKey();
    }
  }, [importMissing]);

  // Shared reset of the per-attempt tracking state. `importId`, `starting`, and
  // polling are managed explicitly by each of start/resume/reset, which differ
  // on those; everything else clears the same way.
  const clearTrackingState = useCallback(() => {
    setStartError(null);
    setStartedSnapshot(null);
    setFailedPolls(0);
  }, []);

  const start = useCallback(
    async (uploadedFileKey: string) => {
      clearTrackingState();
      setImportId(null);
      setStarting(true);
      // A new upload supersedes any previously-saved job; only re-persist on success.
      clearImportJobKey();
      try {
        const result = await startImport({ variables: { input: { uploadedFileKey } } });
        const created = result.data?.rescueStartImport;
        if (!created?.id) {
          setStartError('The server did not return an import to track.');
          return;
        }
        setStartedSnapshot(created);
        setImportId(created.id);
        // Persist the id so the user can close the wizard and resume tracking
        // this job (within 30 min) instead of starting over.
        writeImportJobKey(created.id);
      } catch (err) {
        setStartError(err instanceof Error ? err.message : 'Failed to start the import.');
      } finally {
        setStarting(false);
      }
    },
    [clearTrackingState, startImport],
  );

  // Resume polling an already-running import by id (e.g. from the saved job
  // cookie when the wizard reopens), without re-running the start mutation.
  const resume = useCallback(
    (existingImportId: string) => {
      clearTrackingState();
      setStarting(false);
      setImportId(existingImportId);
    },
    [clearTrackingState],
  );

  const reset = useCallback(() => {
    stopPolling();
    clearTrackingState();
    setStarting(false);
    setImportId(null);
  }, [clearTrackingState, stopPolling]);

  // Derive the coarse phase. Start-mutation failure and a terminal
  // FAILED both surface as `failed`; the message differs.
  let phase: ImportProgressPhase;
  let errorMessage: string | null = null;
  if (startError) {
    phase = 'failed';
    errorMessage = startError;
  } else if (starting) {
    phase = 'starting';
  } else if (!importId) {
    phase = 'idle';
  } else if (progress?.status === 'SUCCEEDED') {
    phase = 'succeeded';
  } else if (progress?.status === 'FAILED') {
    phase = 'failed';
    errorMessage = progress.terminalError ?? 'The import failed while processing. No pets were imported.';
  } else if (trackingStalled) {
    // Polling kept failing (or the import couldn't be found) past the cap —
    // give up rather than spin forever. We keep the saved job cookie (only a
    // terminal status clears it), so reopening Import within 30 min resumes
    // this tracker; the worker also runs server-side regardless of this view.
    phase = 'failed';
    errorMessage =
      "We couldn't reach this import for a while. It may still be processing in the background — reopen Import to check its progress, or look for the pets in your list before re-uploading.";
  } else {
    phase = 'tracking';
  }

  // Soft "couldn't refresh" hint while still tracking through transient poll
  // failures (before we hit the give-up cap). Cleared on recovery or once we
  // transition to the failed state above.
  const pollError = phase === 'tracking' && failedPolls > 0;

  return { phase, progress, errorMessage, pollError, start, resume, importMissing, reset };
}
